import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Confirm, LanguageSelect, languageOptions, toast, useEnabledLanguages,
  type Destructive, type EnabledLanguage,
} from '@drbell/ui';
import { api, type PatientDetail, type PatientSummary } from '../api.ts';
import { browserZone, modesOffered, zoneChoices, zoneName, type Mode } from '../self-service.ts';
import { ModeChoice } from './SetUp.tsx';

/**
 * A record's own settings — what the phone keeps in Daily routine and
 * Profile → the person's details, on the website.
 *
 * The name (and its first and last parts, a date of birth and an address,
 * as the phone's PersonDetails), the language the reminders speak, the
 * time zone they ring in, how the screen looks on the person's own phone,
 * whose phone the record is on, and — for whoever owns it — erasing it.
 * Each through the endpoint the phone uses (`PUT /patients/:id` and
 * friends); the server checks `patient.edit` on every one, and this page
 * only offers what the reader may do.
 */
export function RecordSettings({ patient, flags, onChanged, onErased }: {
  patient: PatientSummary;
  flags: Record<string, unknown>;
  /** The name, language or zone changed: the switcher and header must hear. */
  onChanged: () => void;
  onErased: () => void;
}) {
  const [detail, setDetail] = useState<PatientDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState<Destructive | null>(null);
  const [names, setNames] = useState({ displayName: '', givenName: '', familyName: '', dateOfBirth: '', address: '' });

  const load = useCallback(async () => {
    try {
      const d = await api.get<PatientDetail>(`/api/patients/${patient.id}`);
      setDetail(d);
      setNames({
        displayName: d.patient.displayName,
        givenName: d.patient.givenName ?? '',
        familyName: d.patient.familyName ?? '',
        dateOfBirth: d.patient.dateOfBirth ?? '',
        address: d.patient.address ?? '',
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [patient.id]);
  useEffect(() => { void load(); }, [load]);

  const loadLanguages = useCallback(() => api.get<{ locales: EnabledLanguage[] }>('/api/i18n/locales'), []);
  const enabled = useEnabledLanguages(loadLanguages);
  const languages = useMemo(() => languageOptions(enabled, detail?.patient.locale ?? 'en-IN'), [enabled, detail?.patient.locale]);

  if (error) return <p role="alert" className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-500">{error}</p>;
  if (!detail) return <p className="text-(--muted)">Loading…</p>;

  const p = detail.patient;
  const can = (c: string) => detail.access.capabilities.includes(c);
  const canEdit = can('patient.edit');
  const isSelf = patient.isSelf === true;
  const thisZone = browserZone();

  const save = async (body: Record<string, unknown>, done: string, detailText?: string) => {
    setBusy(true);
    try {
      await api.put(`/api/patients/${patient.id}`, body);
      toast.success(done, detailText);
      await load();
      onChanged();
    } catch (e) {
      toast.error('Could not save', e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const namesChanged =
    names.displayName.trim() !== p.displayName ||
    names.givenName.trim() !== (p.givenName ?? '') ||
    names.familyName.trim() !== (p.familyName ?? '') ||
    names.dateOfBirth !== (p.dateOfBirth ?? '') ||
    names.address.trim() !== (p.address ?? '');

  const field = 'mt-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--bg) px-3 disabled:opacity-60';
  const heading = 'text-xs font-semibold uppercase tracking-wide text-(--muted)';

  return (
    <section className="space-y-8">
      <h2 className="text-lg font-semibold">{isSelf ? 'Your settings' : `${p.displayName}’s settings`}</h2>
      {!canEdit && (
        <p className="rounded-lg border border-(--brand) bg-(--brand-soft) px-3 py-2 text-sm">
          Only someone who manages {p.displayName}&rsquo;s medicines can change this.
        </p>
      )}

      {/* Who this is: the label every screen shows, and its parts. */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!namesChanged || !names.displayName.trim()) return;
          void save({
            displayName: names.displayName.trim(),
            givenName: names.givenName.trim() || null,
            familyName: names.familyName.trim() || null,
            dateOfBirth: names.dateOfBirth || null,
            address: names.address.trim() || null,
          }, 'Details saved');
        }}
      >
        <h3 className={heading}>Name and details</h3>
        <label className="mt-2 block">
          <span className="text-xs text-(--muted)">Name, as every screen shows it</span>
          <input disabled={!canEdit} value={names.displayName} maxLength={120} onChange={(e) => setNames({ ...names, displayName: e.target.value })} className={field} />
        </label>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="text-xs text-(--muted)">First name (optional)</span>
            <input disabled={!canEdit} value={names.givenName} maxLength={80} onChange={(e) => setNames({ ...names, givenName: e.target.value })} className={field} />
          </label>
          <label className="block">
            <span className="text-xs text-(--muted)">Last name (optional)</span>
            <input disabled={!canEdit} value={names.familyName} maxLength={80} onChange={(e) => setNames({ ...names, familyName: e.target.value })} className={field} />
          </label>
          <label className="block">
            <span className="text-xs text-(--muted)">Date of birth (optional)</span>
            <input disabled={!canEdit} type="date" value={names.dateOfBirth} onChange={(e) => setNames({ ...names, dateOfBirth: e.target.value })} className={field} />
          </label>
        </div>
        <label className="mt-3 block">
          <span className="text-xs text-(--muted)">Address (optional) — read by whoever comes to help in an emergency</span>
          <textarea disabled={!canEdit} value={names.address} maxLength={400} rows={2} onChange={(e) => setNames({ ...names, address: e.target.value })} className={`${field} py-2`} />
        </label>
        {canEdit && namesChanged && (
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => void load()} className="min-h-11 rounded-lg border border-(--line) px-4 text-sm">Cancel</button>
            <button type="submit" disabled={busy || !names.displayName.trim()} className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40">
              Save details
            </button>
          </div>
        )}
      </form>

      <div>
        <h3 className={heading}>Language</h3>
        <p className="mt-1 text-sm text-(--muted)">
          Reminders and read-aloud use this language. Medicine names always stay exactly as the doctor wrote them.
        </p>
        <LanguageSelect
          className="mt-2"
          value={p.locale}
          options={languages}
          disabled={!canEdit || busy}
          onChange={(code) => {
            if (code === p.locale) return;
            void (async () => {
              await save({ locale: code }, 'Language saved');
              // Same person, same language — or their phone reverts at its next launch (defect 114).
              if (isSelf) await api.put('/api/me/profile', { locale: code }).catch(() => undefined);
            })();
          }}
        />
      </div>

      <div>
        <h3 className={heading}>Time zone</h3>
        <p className="mt-1 text-sm text-(--muted)">Doses follow this time zone: {zoneName(p.timezone)}.</p>
        {canEdit && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <select
              aria-label="Time zone"
              value={p.timezone}
              disabled={busy}
              onChange={(e) => {
                const zone = e.target.value;
                // Recorded with the reason and where this browser was (RX-REM-16).
                void save({ timezone: zone, timezoneReason: 'manual', deviceTimezone: thisZone }, 'Time zone saved', 'Every future reminder has moved to the new time zone.');
              }}
              className="min-h-11 rounded-lg border border-(--line) bg-(--bg) px-3"
            >
              {zoneChoices(p.timezone, thisZone).map((z) => (
                <option key={z} value={z}>{zoneName(z)}{z === thisZone ? ' — this computer' : ''}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div>
        <h3 className={heading}>How the screen looks</h3>
        <p className="mt-1 text-sm text-(--muted)">
          Takes effect on {isSelf ? 'your' : 'their'} own phone. Anyone else looking after {isSelf ? 'you' : 'them'} sees the ordinary screen.
        </p>
        <ModeChoice
          value={p.presentationMode}
          modes={modesOffered(flags, p.presentationMode)}
          disabled={!canEdit || busy}
          onChange={(m: Mode) => { if (m !== p.presentationMode) void save({ presentationMode: m }, 'Saved'); }}
        />
      </div>

      {/* Whose phone this record belongs to — RX-ONB-03. */}
      <div>
        <h3 className={heading}>{isSelf ? 'Your own phone' : 'Their own phone'}</h3>
        <p className="mt-1 text-sm text-(--muted)">
          {p.selfUserId
            ? isSelf
              ? 'This is your own record. Its reminders ring in the DrBell app on your phone.'
              : `${p.displayName} has this on their own phone, which shows the screen chosen above.`
            : `Not on ${p.displayName}’s own phone yet. Invite their number as “Their own phone” in Who looks after them.`}
        </p>
        {canEdit && p.selfUserId && !isSelf && (
          <button
            disabled={busy}
            onClick={() => setAsk({
              title: 'Unlink their phone?',
              body: `${p.displayName}’s phone stops being theirs to use for this record.`,
              reassurance: 'Nothing in the record changes, and you can invite a new number afterwards.',
              confirmLabel: 'Unlink their phone',
              onConfirm: async () => {
                await api.del(`/api/patients/${patient.id}/self`);
                await load();
                onChanged();
              },
            })}
            className="mt-2 min-h-11 rounded-lg border border-(--line) px-4 text-sm hover:border-(--accent)"
          >
            Unlink their phone
          </button>
        )}
      </div>

      {/*
        Removing a person — RX-TRUST-03. Owner only (`patient.delete`), never
        one's own record (an account is deleted from the account), and the
        strongest guard in the product: the name, typed.
      */}
      {can('patient.delete') && !isSelf && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-red-500">Remove {p.displayName}</h3>
          <p className="mt-1 text-sm text-(--muted)">
            Erases {p.displayName}&rsquo;s medicines, history, photographs and caregivers, straight away and for good.
            It cannot be undone.
          </p>
          <button
            onClick={() => setAsk({
              title: `Erase ${p.displayName}?`,
              body: `Erases ${p.displayName}’s medicines, history, photographs and caregivers, straight away and for good. It cannot be undone.`,
              reassurance: 'The accounts of the people who looked after them are not touched.',
              confirmLabel: `Erase ${p.displayName}`,
              typeToConfirm: p.displayName,
              onConfirm: async () => {
                await api.del(`/api/patients/${patient.id}`);
                toast.success(`${p.displayName} has been erased.`);
                onErased();
              },
            })}
            className="mt-2 min-h-11 rounded-lg border border-red-500/50 px-4 text-sm text-red-500 hover:border-red-500"
          >
            Erase {p.displayName}
          </button>
        </div>
      )}

      {ask && <Confirm {...ask} onCancel={() => setAsk(null)} />}
    </section>
  );
}
